/**
 * Reusable wizard entries list component.
 * Used in AdminWizardDetail (entries tab) and AdminUserDetail.
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listWizardInstances, bulkDeleteWizardInstances, type WizardInstanceSummary } from "../../services/naraDoor";
import { adminRoutes } from "../../lib/routes";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { KiiList, type KiiListColumn, KiiDialogLayout } from "../kii";
import { TableRow, TableCell } from "../ui/table";
import { Wand2, Trash2, Loader2, AlertTriangle } from "lucide-react";

interface WizardEntriesListProps {
  templateId?: string;
  userId?: string;
  showUserColumn?: boolean;
  showWizardColumn?: boolean;
}

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  abandoned: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  timed_out: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] || "bg-muted text-muted-foreground";
  const label = status.replace(/_/g, " ");
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${style}`}>
      {label}
    </span>
  );
}

export function WizardEntriesList({ templateId, userId, showUserColumn, showWizardColumn }: WizardEntriesListProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatDate } = useDateFormatter();
  const [skip, setSkip] = useState(0);
  const limit = 20;

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["wizardInstances", templateId, userId, skip, limit],
    queryFn: () => listWizardInstances({ templateId, userId, limit, offset: skip }),
  });

  const items = data?.items || [];
  const total = data?.total || 0;

  const columns: KiiListColumn[] = [
    { key: "select", label: "" },
    ...(showUserColumn ? [{ key: "user", label: "User" }] : []),
    ...(showWizardColumn ? [{ key: "wizard", label: "Wizard" }] : []),
    { key: "status", label: "Status" },
    { key: "step", label: "Step" },
    { key: "xp", label: "XP" },
    { key: "started", label: "Started" },
    { key: "completed", label: "Completed" },
  ];

  const handleRowClick = useCallback((entry: WizardInstanceSummary) => {
    navigate(adminRoutes.wizardEntry(entry.instance_id));
  }, [navigate]);

  const handlePageChange = useCallback((newSkip: number) => {
    setSkip(newSkip);
    setSelectedIds(new Set());
  }, []);

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.instance_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await bulkDeleteWizardInstances(Array.from(selectedIds));
      setSelectedIds(new Set());
      setShowDeleteDialog(false);
      queryClient.invalidateQueries({ queryKey: ["wizardInstances"] });
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center gap-3 p-3 bg-muted/50 border border-border rounded-lg">
          <input
            type="checkbox"
            checked={items.length > 0 && selectedIds.size === items.length}
            ref={(el) => {
              if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < items.length;
            }}
            onChange={toggleSelectAll}
            className="h-4 w-4 rounded border-border cursor-pointer"
          />
          <span className="text-sm text-foreground font-medium">
            {selectedIds.size} selected
          </span>
          <button
            onClick={() => { setDeleteError(null); setShowDeleteDialog(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/10 border border-destructive/30 rounded-lg transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-muted-foreground hover:text-foreground ml-auto"
          >
            Clear selection
          </button>
        </div>
      )}

      <KiiList<WizardInstanceSummary>
        columns={columns}
        data={items}
        isLoading={isLoading}
        emptyMessage="No wizard entries found"
        emptyIcon={<Wand2 className="h-10 w-10 text-muted-foreground" />}
        showCount
        countLabel={(count) => `${count} entr${count !== 1 ? "ies" : "y"}`}
        onRowClick={handleRowClick}
        serverPagination={{
          totalCount: total,
          skip,
          limit,
        }}
        onPageChange={handlePageChange}
        renderRow={(entry) => (
          <TableRow key={entry.instance_id}>
            <TableCell className="w-10">
              <input
                type="checkbox"
                checked={selectedIds.has(entry.instance_id)}
                onClick={(e) => toggleSelect(entry.instance_id, e)}
                onChange={() => {}}
                className="h-4 w-4 rounded border-border cursor-pointer"
              />
            </TableCell>
            {showUserColumn && (
              <TableCell>
                <span className="font-medium">{entry.user_display_name || entry.user_id}</span>
              </TableCell>
            )}
            {showWizardColumn && (
              <TableCell>
                <span className="font-medium">{entry.template_name}</span>
              </TableCell>
            )}
            <TableCell>
              <StatusBadge status={entry.status} />
            </TableCell>
            <TableCell className="text-muted-foreground">
              {entry.current_step_index + 1}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {entry.total_xp ?? "\u2014"}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {entry.started_at ? formatDate(entry.started_at, "date") : "\u2014"}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {entry.completed_at ? formatDate(entry.completed_at, "date") : "\u2014"}
            </TableCell>
          </TableRow>
        )}
      />

      {/* Delete Confirmation Dialog */}
      <KiiDialogLayout
        open={showDeleteDialog}
        onOpenChange={(open) => { if (!open) setShowDeleteDialog(false); }}
        title="Delete Wizard Entries"
        description={`Permanently delete ${selectedIds.size} wizard entr${selectedIds.size !== 1 ? "ies" : "y"}? This action cannot be undone.`}
        size="sm"
        footer={
          <>
            <button
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-destructive hover:bg-destructive/90 rounded-lg transition-colors disabled:opacity-50"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </>
        }
      >
        <div className="flex items-start gap-3 mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="text-sm text-destructive">
            This will permanently delete {selectedIds.size} wizard entr{selectedIds.size !== 1 ? "ies" : "y"} and terminate any running workflows. This cannot be undone.
          </div>
        </div>
        {deleteError && (
          <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
            {deleteError}
          </div>
        )}
      </KiiDialogLayout>
    </>
  );
}
